import type { Capability } from "@/types/content";

/**
 * Status reflects the real, evidenced state of each capability area, not aspiration.
 * Update statuses only when the underlying engineering state changes — see
 * docs/CONTENT_GUIDE.md for how these map to internal engineering evidence.
 *
 * Grouped as Build / Execute / Govern / Extend on /product (see the
 * 2026-07-18 brand redesign pass) rather than one flat, equal-size grid.
 */
export const productCapabilities: Capability[] = [
  {
    id: "canvas",
    title: "Visual engineering canvas",
    description:
      "A canvas of rounded-square tiles for composing software visually. Each tile is a real engineering capability with inputs, outputs and state, inspectable at a glance and backed by an authoritative model, not a picture that only looks like software.",
    status: "pilot",
    group: "build",
    keycap: "connect",
  },
  {
    id: "workspaces",
    title: "Workspaces and projects",
    description:
      "Projects group the tiles, connections and configuration an engineer is actively working on. Workspace organization is being extended from individual projects toward shared team workspaces.",
    status: "in-development",
    group: "build",
    keycap: "workspace",
  },
  {
    id: "components",
    title: "Components and reusable libraries",
    description:
      "Software is composed from versioned, typed component tiles rather than ad hoc scripts. A foundation library of common component types is defined; a shippable catalogue of ready-to-use components is still being built out.",
    status: "in-development",
    group: "build",
    keycap: "compose",
  },
  {
    id: "git",
    title: "Git and source control",
    description:
      "Source-control-aware workflows are part of Devory's engineering direction, wiring source history directly into the canvas as a tile. This is planned rather than shipped today.",
    status: "planned",
    group: "build",
    keycap: "git",
  },
  {
    id: "terminal",
    title: "Terminal and developer tools",
    description:
      "Direct terminal access alongside the visual canvas is planned so engineers can drop into familiar command-line workflows without leaving Devory.",
    status: "planned",
    group: "build",
    keycap: "terminal",
  },
  {
    id: "runtime",
    title: "Runtime and execution",
    description:
      "A deterministic local execution engine plans, schedules, runs and journals tile executions as real processes, with recovery from ordinary failures. Execution is currently limited to trusted, known components.",
    status: "pilot",
    group: "execute",
    keycap: "runtime",
  },
  {
    id: "deployments",
    title: "Deployments and environments",
    description:
      "Devory tracks deployment and runtime state as part of its cloud services. Binding that state to live, customer-controlled cloud environments is in progress rather than generally available.",
    status: "in-development",
    group: "execute",
    keycap: "deployment",
  },
  {
    id: "desktop-cloud",
    title: "Desktop and cloud relationship",
    description:
      "Devory Desktop is the local engineering canvas; Devory Cloud is the shared canvas for identity, collaboration and marketplace services. Connecting the two into one consistent experience is active, ongoing work.",
    status: "in-development",
    group: "execute",
    keycap: "cloud",
  },
  {
    id: "teams",
    title: "Teams, organisations and permissions",
    description:
      "Identity and organisation services are live in the customer-facing product: self-serve sign-up, personal and team-scoped workspaces, and role-aware membership (Owner, Admin, BillingAdmin, Member, Viewer). Studio and Team plans add approval workflows for sensitive role and membership changes.",
    status: "live",
    group: "govern",
    keycap: "identity",
  },
  {
    id: "marketplace",
    title: "Marketplace catalogue",
    description:
      "The Devory Marketplace is a public catalogue of verified components, capabilities, providers, templates and integrations. Browsing is public today, and the real publisher/listing/ordering pipeline -- publisher accounts, component authoring and publishing, entitlement-gated orders and commission accounting -- is live, free at every plan. No listings have been published to the public catalogue yet.",
    status: "live",
    group: "extend",
    keycap: "publish",
  },
  {
    id: "extensions",
    title: "Extensions, plugins and MCP",
    description:
      "An extensible plugin architecture, including support for MCP-style tool integrations, is planned so third-party tiles can join the canvas under a governed trust model.",
    status: "planned",
    group: "extend",
    keycap: "extension",
  },
  {
    id: "ai-assisted",
    title: "AI-assisted engineering",
    description:
      "An AI agent doesn't need to reinvent common infrastructure to work inside Devory. Through the Autonomous User Interface (AUI), it operates as a typed, permissioned participant that can discover qualified components and compose them into a working system, under explicit delegation, confirmation and audit boundaries. That is distinct from unsupervised automation, and it is one capability inside the system, not the system itself.",
    status: "in-development",
    group: "extend",
    keycap: "ai",
  },
];
